import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../App';
import toast from 'react-hot-toast';
import { BookOpenIcon, ArrowRightIcon } from '@heroicons/react/24/solid';
import axios from 'axios';

const JoinClassroom = () => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { classroomCode } = useParams();

  // If code is in URL, auto-fill it
  useEffect(() => {
    if (classroomCode) {
      setCode(classroomCode.toUpperCase());
    }
  }, [classroomCode]);

  const handleJoinClassroom = async (e) => {
    e.preventDefault();

    if (!code || code.trim().length === 0) {
      toast.error('Please enter a classroom code');
      return;
    }

    if (code.trim().length !== 8) {
      toast.error('Classroom code must be 8 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/api/classrooms/join', {
        code: code.toUpperCase(),
        student_id: user.id
      });

      if (response.data.alreadyEnrolled) {
        toast.success('You are already enrolled in this classroom!');
      } else {
        toast.success(`Successfully joined ${response.data.classroom.name}!`);
      }

      // Navigate to the classroom
      navigate(`/classroom/${response.data.classroom.id}`);
    } catch (error) {
      console.error('Error joining classroom:', error);
      toast.error(error.response?.data?.error || 'Invalid classroom code');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    // Convert to uppercase and limit to 8 characters
    setCode(e.target.value.toUpperCase().slice(0, 8));
  };

  return (
    <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="bg-primary-100 dark:bg-primary-900 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookOpenIcon className="h-8 w-8 text-primary-600 dark:text-primary-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Join Classroom</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Enter the classroom code provided by your tutor
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleJoinClassroom}>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Classroom Code
              </label>
              <input
                type="text"
                value={code}
                onChange={handleCodeChange}
                placeholder="ABC123XY"
                className="w-full px-4 py-3 text-center text-2xl font-mono font-bold tracking-widest uppercase border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                maxLength={8}
                disabled={loading}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                The code is 8 characters long
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || code.length !== 8}
              className={`w-full flex items-center justify-center px-4 py-3 rounded-lg font-semibold transition-colors ${
                loading || code.length !== 8
                  ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-primary-600 dark:bg-primary-700 text-white hover:bg-primary-700 dark:hover:bg-primary-600'
              }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Joining...
                </>
              ) : (
                <>
                  Join Classroom
                  <ArrowRightIcon className="h-5 w-5 ml-2" />
                </>
              )}
            </button>
          </form>

          {/* Back to Dashboard */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              Back to Dashboard
            </button>
          </div>

          {/* Help Text */}
          <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-sm text-blue-900 dark:text-blue-200">
              <strong>Don't have a code?</strong> Ask your tutor to send you a classroom invitation
              or provide the classroom code.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JoinClassroom;
